// Daily Challenges Service
// Creates daily goals to keep users engaged and coming back

import { logger } from '../utils/logger';
import { aiCoach } from './aiCoach';
import { notificationService } from './notificationService';

export interface DailyChallenge {
  id: string;
  type: 'questions' | 'topics' | 'score' | 'tests';
  title: string;
  description: string;
  target: number;
  current: number;
  reward: number; // Knowledge Points reward
  completed: boolean;
  date: string; // ISO date string
}

export interface DailyChallengeData {
  challenges: DailyChallenge[];
  lastUpdateDate: string;
  completedToday: number;
  totalRewards: number;
}

class DailyChallengesService {
  private readonly STORAGE_KEY = 'daily_challenges_data';

  /**
   * Get or create today's challenges
   */
  getTodaysChallenges(): DailyChallenge[] {
    const data = this.loadData();
    const today = new Date().toISOString().split('T')[0];

    // If challenges exist for today, return them
    if (data.lastUpdateDate === today && data.challenges.length > 0) {
      return this.updateChallengeProgress(data.challenges);
    }

    // Create new challenges for today
    const newChallenges = this.generateDailyChallenges();
    this.saveData({
      challenges: newChallenges,
      lastUpdateDate: today,
      completedToday: 0,
      totalRewards: data.totalRewards || 0
    });

    return newChallenges;
  }

  /**
   * Generate daily challenges based on user progress
   */
  private generateDailyChallenges(): DailyChallenge[] {
    const testHistory = aiCoach.getTestHistory();
    const testScores = aiCoach.getTestScores();
    const weakAreas = Object.entries(testScores)
      .filter(([_, data]) => data.average < 60)
      .map(([testId]) => testId);

    const challenges: DailyChallenge[] = [];

    // Challenge 1: Answer questions (always available)
    challenges.push({
      id: 'daily-questions',
      type: 'questions',
      title: 'Daily Practice',
      description: 'Answer 10 questions today',
      target: 10,
      current: 0,
      reward: 50,
      completed: false,
      date: new Date().toISOString().split('T')[0]
    });

    // Challenge 2: Complete a practice test (if user has started)
    if (testHistory.length > 0) {
      challenges.push({
        id: 'daily-test',
        type: 'tests',
        title: 'Test Master',
        description: 'Complete 1 practice test',
        target: 1,
        current: 0,
        reward: 75,
        completed: false,
        date: new Date().toISOString().split('T')[0]
      });
    }

    // Challenge 3: Improve weak area (if user has weak areas)
    if (weakAreas.length > 0) {
      const topWeakArea = weakAreas[0];
      challenges.push({
        id: 'daily-weak-area',
        type: 'topics',
        title: 'Weak Area Focus',
        description: `Practice ${topWeakArea} today`,
        target: 1,
        current: 0,
        reward: 100,
        completed: false,
        date: new Date().toISOString().split('T')[0]
      });
    }

    return challenges;
  }

  /**
   * Update challenge progress based on current user activity
   */
  private updateChallengeProgress(challenges: DailyChallenge[]): DailyChallenge[] {
    const testHistory = aiCoach.getTestHistory();
    const today = new Date().toISOString().split('T')[0];
    
    // Get today's activity
    const todayTests = testHistory.filter(t => t.date?.startsWith(today));
    const todayQuestions = todayTests.reduce((sum, t) => sum + t.totalQuestions, 0);

    return challenges.map(challenge => {
      if (challenge.completed) return challenge;

      let current = 0;
      
      switch (challenge.type) {
        case 'questions':
          current = todayQuestions;
          break;
        case 'tests':
          current = todayTests.length;
          break;
        case 'topics':
          // Check if user practiced the weak area topic today
          const topicTests = todayTests.filter(t => t.testId === challenge.description.split(' ')[1]);
          current = topicTests.length > 0 ? 1 : 0;
          break;
        case 'score':
          // Average score from today's tests
          if (todayTests.length > 0) {
            const avg = todayTests.reduce((sum, t) => sum + t.percentage, 0) / todayTests.length;
            current = avg >= challenge.target ? 1 : 0;
          }
          break;
      }

      const completed = current >= challenge.target;
      
      return {
        ...challenge,
        current,
        completed
      };
    });
  }

  /**
   * Check if challenge is completed and award reward
   */
  checkAndRewardChallenge(challengeId: string): { completed: boolean; reward?: number } {
    const challenges = this.getTodaysChallenges();
    const challenge = challenges.find(c => c.id === challengeId);

    if (!challenge || challenge.completed) {
      return { completed: false };
    }

    const updatedChallenges = this.updateChallengeProgress(challenges);
    const updatedChallenge = updatedChallenges.find(c => c.id === challengeId);

    if (updatedChallenge && updatedChallenge.completed && !challenge.completed) {
      // Challenge just completed - award reward
      const data = this.loadData();
      this.saveData({
        ...data,
        completedToday: data.completedToday + 1,
        totalRewards: data.totalRewards + updatedChallenge.reward
      });

      logger.info(`Daily challenge completed: ${challengeId}, reward: ${updatedChallenge.reward} KP`);
      
      // Show notification for daily goal completion (questions challenge)
      if (challengeId === 'daily-questions') {
        notificationService.showDailyGoalCompletionNotification(updatedChallenge.target);
      }
      
      return { completed: true, reward: updatedChallenge.reward };
    }

    return { completed: false };
  }

  /**
   * Get progress for all challenges
   */
  getChallengeProgress(): { completed: number; total: number; progress: number } {
    const challenges = this.getTodaysChallenges();
    const completed = challenges.filter(c => c.completed).length;
    const total = challenges.length;
    const progress = total > 0 ? (completed / total) * 100 : 0;

    return { completed, total, progress };
  }

  /**
   * Load data from storage
   */
  private loadData(): DailyChallengeData {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return {
          challenges: [],
          lastUpdateDate: '',
          completedToday: 0,
          totalRewards: 0
        };
      }
      return JSON.parse(stored) as DailyChallengeData;
    } catch (error) {
      logger.error('Error loading daily challenges data:', error);
      return {
        challenges: [],
        lastUpdateDate: '',
        completedToday: 0,
        totalRewards: 0
      };
    }
  }

  /**
   * Save data to storage
   */
  private saveData(data: DailyChallengeData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      logger.error('Error saving daily challenges data:', error);
    }
  }
}

export const dailyChallengesService = new DailyChallengesService();

